import { useState, useCallback, useEffect, useRef } from 'react';
import { notification } from 'antd';
import lib from '../../../assets/utils/lib';

const DEFAULT_META = { total: 0, page: 1, limit: 20 };

/**
 * @param {object} options
 * @param {string} [options.initialStatus='open']
 * @param {string} [options.initialAction='add'] — `add` negotiations | `remove` tickets
 * @param {number|string|null} [options.scopedOrgId]
 */
export function usePlanRequests({
  initialStatus = 'open',
  initialAction = 'add',
  scopedOrgId = null,
} = {}) {
  const [status, setStatus] = useState(initialStatus);
  const [action, setAction] = useState(initialAction);
  const [filterOrgId, setFilterOrgId] = useState(null);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(DEFAULT_META);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [boardCounts, setBoardCounts] = useState({ add: 0, remove: 0, total: 0 });

  const effectiveOrgId = scopedOrgId != null && scopedOrgId !== ''
    ? scopedOrgId
    : filterOrgId;

  const latestRef = useRef({
    status,
    action,
    page: 1,
    limit: 20,
    orgId: effectiveOrgId,
  });

  latestRef.current = {
    status,
    action,
    page: meta.page,
    limit: meta.limit,
    orgId: effectiveOrgId,
  };

  const refreshBoardCounts = useCallback(async () => {
    try {
      const counts = await lib.getOpenPlanRequestCounts(effectiveOrgId);
      setBoardCounts(counts);
      return counts;
    } catch (_) {
      return { add: 0, remove: 0, total: 0 };
    }
  }, [effectiveOrgId]);

  const fetchRequests = useCallback(async (overrides = {}) => {
    const statusVal = overrides.status ?? latestRef.current.status;
    const actionVal = overrides.action ?? latestRef.current.action;
    const page = overrides.page ?? latestRef.current.page;
    const limit = overrides.limit ?? latestRef.current.limit;
    const orgIdVal = overrides.org_id !== undefined
      ? overrides.org_id
      : latestRef.current.orgId;

    setLoading(true);
    setError(null);

    try {
      const query = { status: statusVal, page, limit };
      if (actionVal === 'add' || actionVal === 'remove') query.action = actionVal;
      if (orgIdVal != null && orgIdVal !== '') query.org_id = orgIdVal;

      const resp = await lib.getPlanRequests(query);
      const parsed = lib.parseApiEnvelope(resp);

      if (!parsed.ok) {
        const msg = parsed.message || 'Failed to load plan requests';
        setError(msg);
        setRows([]);
        setMeta({ total: 0, page, limit });
        return { ok: false, message: msg };
      }

      const payload = parsed.data ?? {};
      const nextRows = Array.isArray(payload.rows)
        ? payload.rows
        : (Array.isArray(payload) ? payload : []);
      const nextMeta = payload.meta ?? parsed.meta ?? { total: nextRows.length, page, limit };

      setRows(nextRows);
      setMeta({
        total: Number(nextMeta.total ?? nextRows.length) || 0,
        page: Number(nextMeta.page ?? page) || page,
        limit: Number(nextMeta.limit ?? limit) || limit,
      });
      refreshBoardCounts();
      return { ok: true, rows: nextRows, meta: nextMeta };
    } catch (e) {
      const msg = e?.message || 'Failed to load plan requests';
      setError(msg);
      setRows([]);
      return { ok: false, message: msg };
    } finally {
      setLoading(false);
    }
  }, [refreshBoardCounts]);

  useEffect(() => {
    fetchRequests({ status, action, page: 1, org_id: effectiveOrgId });
  }, [status, action, effectiveOrgId, fetchRequests]);

  const changeStatus = useCallback((nextStatus) => {
    setStatus(nextStatus);
    setMeta((prev) => ({ ...prev, page: 1 }));
  }, []);

  const changeAction = useCallback((nextAction) => {
    setAction(nextAction);
    setStatus('open');
    setMeta((prev) => ({ ...prev, page: 1 }));
  }, []);

  const changePage = useCallback((page, pageSize) => {
    const limit = pageSize ?? latestRef.current.limit;
    setMeta((prev) => ({ ...prev, page, limit }));
    fetchRequests({ page, limit });
  }, [fetchRequests]);

  const refetch = useCallback(() => fetchRequests(), [fetchRequests]);

  const setOrgFilter = useCallback((orgId) => {
    if (scopedOrgId != null && scopedOrgId !== '') return;
    setFilterOrgId(orgId);
    setMeta((prev) => ({ ...prev, page: 1 }));
  }, [scopedOrgId]);

  const getRequestDetail = useCallback(async (requestId) => {
    const resp = await lib.getPlanRequest(requestId);
    return lib.parseApiEnvelope(resp);
  }, []);

  const approveRequest = useCallback(async (requestId, { note, force, ...costs } = {}) => {
    const parsed = await lib.approvePlanRequest(requestId, { note, force, ...costs });
    if (parsed.ok) {
      notification.success({
        message: force ? (parsed.message || 'Force-approved') : (parsed.message || 'Request approved'),
        placement: 'topRight',
      });
      await refetch();
    }
    return parsed;
  }, [refetch]);

  const counterOffer = useCallback(async (requestId, payload = {}) => {
    let parsed = await lib.createPlanRequestOffer(requestId, payload);
    if (!parsed.ok && (parsed.httpStatus === 404 || /not found/i.test(String(parsed.message || '')))) {
      parsed = await lib.updatePlanRequest(requestId, payload);
    }
    if (parsed.ok) {
      notification.success({ message: parsed.message || 'Counter offer sent', placement: 'topRight' });
      await refetch();
    } else {
      notification.error({ message: parsed.message || 'Counter failed', placement: 'topRight' });
    }
    return parsed;
  }, [refetch]);

  const updateRequestCosts = useCallback(async (requestId, payload) => {
    return counterOffer(requestId, payload);
  }, [counterOffer]);

  const rejectRequest = useCallback(async (requestId, note) => {
    const parsed = await lib.rejectPlanRequest(requestId, { note });
    if (parsed.ok) {
      notification.success({ message: parsed.message || 'Request rejected', placement: 'topRight' });
      await refetch();
      return parsed;
    }
    notification.error({ message: parsed.message || 'Reject failed', placement: 'topRight' });
    return parsed;
  }, [refetch]);

  const checkRemoval = useCallback(async (socialNo, planId) => {
    const resp = await lib.checkOrgPlanRemoval(socialNo, planId);
    return lib.parseApiEnvelope(resp);
  }, []);

  return {
    status,
    setStatus: changeStatus,
    action,
    setAction: changeAction,
    boardCounts,
    orgId: effectiveOrgId,
    setOrgFilter,
    orgFilterLocked: scopedOrgId != null && scopedOrgId !== '',
    rows,
    meta,
    loading,
    error,
    fetchRequests,
    changePage,
    refetch,
    getRequestDetail,
    approveRequest,
    rejectRequest,
    counterOffer,
    updateRequestCosts,
    checkRemoval,
  };
}

export default usePlanRequests;
